import React, { useMemo } from 'react';
import { Accordion, Button, Table } from 'react-bootstrap';
import { Download } from 'react-bootstrap-icons';
import { AdminRum, InventoryRun, InventoryRunItem } from '../../types';
import {
  getRumsNeedingDeactivation,
  getRumsNeedingReactivation,
  getDaysSinceAddedReport,
  getInventoryListReport,
  getPossibleReorderReport,
  generateDeactivationCsv,
  generateReactivationCsv,
  generateDaysSinceAddedCsv,
  generateInventoryListCsv,
  generatePossibleReorderCsv,
  downloadCsv,
} from './inventoryUtils';

interface InventoryReportsProps {
  rums: AdminRum[];
  runItems: InventoryRunItem[];
  allRunItems: InventoryRunItem[];
  inventoryRuns: InventoryRun[];
  currentRunId: string;
  finalizedEndDate?: string | null;
}

export default function InventoryReports({
  rums,
  runItems,
  allRunItems,
  inventoryRuns,
  currentRunId,
  finalizedEndDate,
}: InventoryReportsProps): JSX.Element {
  const deactivateList = useMemo(
    () => getRumsNeedingDeactivation(rums, runItems),
    [rums, runItems],
  );
  const reactivateList = useMemo(
    () => getRumsNeedingReactivation(rums, runItems),
    [rums, runItems],
  );
  const daysSinceAdded = useMemo(
    () => getDaysSinceAddedReport(rums, runItems),
    [rums, runItems],
  );
  const inventoryList = useMemo(
    () => getInventoryListReport(rums, runItems),
    [rums, runItems],
  );
  const possibleReorderList = useMemo(
    () => getPossibleReorderReport(rums, inventoryRuns, allRunItems, currentRunId),
    [rums, inventoryRuns, allRunItems, currentRunId],
  );

  return (
    <Accordion flush className="mt-3">
      {/* Report 1: Need to Deactivate */}
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          Need to Deactivate ({deactivateList.length})
        </Accordion.Header>
        <Accordion.Body>
          <Button
            variant="outline-light"
            size="sm"
            className="mb-2"
            onClick={() => downloadCsv(
              'need_to_deactivate.csv',
              generateDeactivationCsv(deactivateList),
            )}
          >
            <Download className="me-1" /> Download CSV
          </Button>
          <Table striped bordered hover variant="dark" size="sm">
            <thead>
              <tr>
                <th>Inventory ID</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {deactivateList.map(rum => (
                <tr key={rum.id}>
                  <td>{rum.inventory_id ?? '—'}</td>
                  <td>{rum.bevager_name}</td>
                </tr>
              ))}
              {deactivateList.length === 0 && (
                <tr>
                  <td colSpan={2} className="text-center text-muted">
                    No rums need deactivation
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>

      {/* Report 2: Need to Reactivate */}
      <Accordion.Item eventKey="1">
        <Accordion.Header>
          Need to Reactivate ({reactivateList.length})
        </Accordion.Header>
        <Accordion.Body>
          <Button
            variant="outline-light"
            size="sm"
            className="mb-2"
            onClick={() => downloadCsv(
              'need_to_reactivate.csv',
              generateReactivationCsv(reactivateList),
            )}
          >
            <Download className="me-1" /> Download CSV
          </Button>
          <Table striped bordered hover variant="dark" size="sm">
            <thead>
              <tr>
                <th>Inventory ID</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {reactivateList.map(rum => (
                <tr key={rum.id}>
                  <td>{rum.inventory_id ?? '—'}</td>
                  <td>{rum.bevager_name}</td>
                </tr>
              ))}
              {reactivateList.length === 0 && (
                <tr>
                  <td colSpan={2} className="text-center text-muted">
                    No rums need reactivation
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>

      {/* Report 3: Days Since Added */}
      <Accordion.Item eventKey="2">
        <Accordion.Header>
          Days Since Added ({daysSinceAdded.length})
        </Accordion.Header>
        <Accordion.Body>
          <Button
            variant="outline-light"
            size="sm"
            className="mb-2"
            onClick={() => downloadCsv(
              'days_since_added.csv',
              generateDaysSinceAddedCsv(daysSinceAdded),
            )}
          >
            <Download className="me-1" /> Download CSV
          </Button>
          <Table striped bordered hover variant="dark" size="sm">
            <thead>
              <tr>
                <th>Inventory ID</th>
                <th>Name</th>
                <th>Date Added</th>
                <th>Days</th>
              </tr>
            </thead>
            <tbody>
              {daysSinceAdded.map(({ rum, days }) => (
                <tr key={rum.id}>
                  <td>{rum.inventory_id ?? '—'}</td>
                  <td>{rum.bevager_name}</td>
                  <td>{new Date(rum.date_added).toLocaleDateString()}</td>
                  <td>{days}</td>
                </tr>
              ))}
              {daysSinceAdded.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>

      {/* Report 4: Inventory List */}
      <Accordion.Item eventKey="3">
        <Accordion.Header>
          Inventory list as of{' '}
          {finalizedEndDate ? new Date(finalizedEndDate).toLocaleDateString() : 'N/A'}
          {' '}
          ({inventoryList.length})
        </Accordion.Header>
        <Accordion.Body>
          <Button
            variant="outline-light"
            size="sm"
            className="mb-2"
            onClick={() => downloadCsv(
              'inventory_list.csv',
              generateInventoryListCsv(inventoryList),
            )}
          >
            <Download className="me-1" /> Download CSV
          </Button>
          <Table striped bordered hover variant="dark" size="sm">
            <thead>
              <tr>
                <th>Inventory ID</th>
                <th>Name</th>
                <th>Country</th>
                <th>Price</th>
                <th>Bottle Count</th>
              </tr>
            </thead>
            <tbody>
              {inventoryList.map(({ rum, count }) => (
                <tr key={rum.id}>
                  <td>{rum.inventory_id ?? '—'}</td>
                  <td>{rum.bevager_name}</td>
                  <td>{rum.origin_country ?? '—'}</td>
                  <td>
                    ${parseFloat(rum.price.toString()).toFixed(0)}
                  </td>
                  <td>{count}</td>
                </tr>
              ))}
              {inventoryList.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No inventory items available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>

      {/* Report 5: Possible re-order */}
      <Accordion.Item eventKey="4">
        <Accordion.Header>
          Possible re-order ({possibleReorderList.length})
        </Accordion.Header>
        <Accordion.Body>
          <Button
            variant="outline-light"
            size="sm"
            className="mb-2"
            onClick={() => downloadCsv(
              'possible_reorder.csv',
              generatePossibleReorderCsv(possibleReorderList),
            )}
          >
            <Download className="me-1" /> Download CSV
          </Button>
          <Table striped bordered hover variant="dark" size="sm">
            <thead>
              <tr>
                <th>Inventory ID</th>
                <th>Name</th>
                <th>Previous Bottle Count</th>
              </tr>
            </thead>
            <tbody>
              {possibleReorderList.map(({ rum, previousCount }) => (
                <tr key={rum.id}>
                  <td>{rum.inventory_id ?? '—'}</td>
                  <td>{rum.bevager_name}</td>
                  <td>{previousCount}</td>
                </tr>
              ))}
              {possibleReorderList.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-muted">
                    No possible re-order rums found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}
